import {
  Activity, ShieldAlert, Brain, HardDrive, Radio, Zap, Layers, Plug,
  Cpu, Wifi, Satellite, Battery, Sun, Network, Database, Workflow,
  LineChart, Building2, Droplets, Factory, Mountain, TrendingUp, Sparkles,
} from "lucide-react";

// Communications options — every range/use-case the platform supports.
export const NEXUS_COMMS = [
  { name: "LoRa", range: "Up to 15 km LoS", role: "Low-power wide-area mesh for remote field assets", icon: Radio },
  { name: "Cellular", range: "Anywhere with coverage", role: "LTE-M / NB-IoT / 4G for unattended sites", icon: Satellite },
  { name: "Wi-Fi Direct", range: "Up to 200 m", role: "High-bandwidth links between dense local nodes", icon: Wifi },
  { name: "GPS / GNSS", range: "Global positioning", role: "Geolocation, geofencing, and asset tracking", icon: Satellite },
];

// Power profiles — flexibility at the edge.
export const NEXUS_POWER = [
  { name: "Battery", spec: "Multi-year, replaceable cells", icon: Battery, note: "Sleep-cycle firmware for years of unattended runtime." },
  { name: "Solar", spec: "Field-rated photovoltaic kit", icon: Sun, note: "Mains-independent at remote, off-grid sites." },
  { name: "Mains / DC", spec: "9 – 36 V DC, 110 – 240 V AC", icon: Plug, note: "Plant-floor friendly with surge isolation and brown-out tolerance." },
];

// Signal / protocol coverage — what a node can ingest natively.
export const NEXUS_PROTOCOLS = [
  { name: "Modbus RTU / TCP", desc: "Talk to existing PLCs, VFDs, energy meters, and BMS controllers." },
  { name: "RS-485 / RS-232", desc: "Native serial bus access for legacy instrumentation." },
  { name: "4 – 20 mA analog", desc: "The industrial backbone — pressure, flow, level, temperature transmitters." },
  { name: "0 – 10 V analog", desc: "HVAC, building automation, drive references." },
  { name: "Pulse counting", desc: "Flow meters, energy meters, rotary encoders, throughput counters." },
  { name: "Dry contact / digital I/O", desc: "Limit switches, relays, valve states, contactor feedback." },
  { name: "I²C / SPI / 1-Wire", desc: "Direct interface to digital sensor packages and edge probes." },
  { name: "Custom drivers", desc: "Onboard scripting hooks let any proprietary protocol be added." },
];

// AI capabilities — what makes Nexus more than a dashboard.
export const NEXUS_AI = [
  {
    title: "Conversational rule authoring",
    copy: "Describe a control intent in natural language and the platform compiles it into a deterministic rule. The rule is back-tested against the past 30 days of telemetry before it can go live — every trigger and every action visible before activation.",
    icon: Workflow,
  },
  {
    title: "Risk and anomaly intelligence",
    copy: "Ask, in plain English, what is most at risk across a fleet, a site, or a single asset. The platform inspects live and historical telemetry, weighs every relevant signal, and replies with ranked findings and recommended actions.",
    icon: ShieldAlert,
  },
  {
    title: "Auto-generated reporting",
    copy: "Shift handovers, compliance reports, executive summaries — written by the platform from raw telemetry, formatted to brand, and scheduled to the right recipients. Hours of manual analysis collapsed into a click.",
    icon: LineChart,
  },
  {
    title: "Predictive maintenance signals",
    copy: "Drifting bearings, leaking seals, climbing draws, flapping sensors — the platform learns asset baselines and surfaces deviations before they become outages.",
    icon: TrendingUp,
  },
];

// Top-line capability rail — the headline pillars on the hub page.
export const NEXUS_PILLARS = [
  {
    title: "Universal signal capture",
    desc: "Every industrial protocol, every analog standard, every digital bus — ingested by a single node and unified into the same channel database.",
    icon: Layers,
  },
  {
    title: "Long-range, multi-protocol mesh",
    desc: "LoRa, cellular, Wi-Fi, GNSS — pick the right radio per site, mix radios per asset, scale beyond 10 km with repeaters where the geography demands it.",
    icon: Network,
  },
  {
    title: "Operates without an uplink",
    desc: "Nodes execute control logic locally and buffer telemetry when the cloud is unreachable. The plant keeps running; the cloud catches up the moment the link returns.",
    icon: Database,
  },
  {
    title: "Autonomous logic at every tier",
    desc: "Hard interlocks live on the node. Cross-asset rules live on the gateway. Site-wide intelligence lives in the cloud. Latency goes where the decision needs to be.",
    icon: ShieldAlert,
  },
  {
    title: "Operator-built widget canvas",
    desc: "Tabs, widgets, density modes, and live channels — composed by the people who actually run the operation. Drag a gauge onto the canvas, bind it to a channel, save the tab. Done.",
    icon: Activity,
  },
  {
    title: "AI co-pilot built in",
    desc: "Plain-English queries, conversational rule authoring, generated reports, and ranked risk findings — every screen has a co-pilot that understands the live operation.",
    icon: Brain,
  },
];

// Industries — proof of cross-vertical breadth.
export const NEXUS_INDUSTRIES = [
  { name: "Water & wastewater", icon: Droplets, copy: "Reservoirs, pump stations, abstraction sites, dosing plants." },
  { name: "Energy & utilities", icon: Zap, copy: "Solar farms, substations, microgrids, demand-response loads." },
  { name: "Mining & minerals", icon: Mountain, copy: "Tailings dams, conveyor health, environmental compliance, vehicle telematics." },
  { name: "Manufacturing", icon: Factory, copy: "Line OEE, energy intensity, predictive maintenance, condition monitoring." },
  { name: "Built environment", icon: Building2, copy: "Commercial HVAC, sub-metering, occupancy, indoor air quality." },
  { name: "Heavy industry", icon: HardDrive, copy: "Steel, cement, petrochem — long-asset-life facilities with mixed PLC vintages." },
];

// Hierarchy — the data model behind the portal.
export const NEXUS_HIERARCHY = [
  { tier: "Organisation", desc: "A tenant. Brand it, permission it, white-label it. The Geminii Nexus header carries the org name as the operator's home." },
  { tier: "Site", desc: "A physical location with one or more gateways, its own tabs, and its own widget canvas. Pump House. Substation. Field 3." },
  { tier: "Gateway", desc: "An on-premise edge appliance that aggregates nodes and runs cross-asset rules locally — independent of the cloud." },
  { tier: "Node", desc: "A field device with multiple inputs and outputs. Every CH (channel) and every RELAY flows through it." },
  { tier: "Channel", desc: "A single addressable measurement — pressure on CH1, flow on CH2, pump state on RELAY1 — sampled, stored, alarmed, and acted upon." },
];

// Hardware showcase — the physical product line.
export const NEXUS_HARDWARE = [
  {
    name: "Nexus Node",
    role: "Edge sensing & control unit",
    icon: Cpu,
    headline: "The field workhorse — IP65, multi-protocol, multi-radio.",
    specs: [
      "8 universal I/O channels (analog + digital + pulse)",
      "On-board scripting engine for local logic",
      "Modbus / RS-485 / SDI-12 bus master",
      "LoRa, Wi-Fi Direct, optional cellular radio",
      "Wide-temperature, IP65-rated, screw-down terminals",
      "Battery, solar, or DC powered — replaceable cell",
    ],
  },
  {
    name: "Nexus Gateway",
    role: "Site aggregator & cross-asset compute",
    icon: HardDrive,
    headline: "The site brain — buffers, decides, uplinks.",
    specs: [
      "Aggregates dozens of nodes per gateway, hundreds with mesh",
      "Edge storage with multi-day buffering on uplink loss",
      "Multi-radio uplink: cellular + Wi-Fi + Ethernet failover",
      "Runs cross-asset rules locally for sub-second response",
      "Signed firmware with staged OTA updates",
      "DIN-rail or wall-mount, plant-floor enclosure",
    ],
  },
  {
    name: "Sensor library",
    role: "Pre-qualified instrumentation",
    icon: Sparkles,
    headline: "Pick the sensor; the platform handles the rest.",
    specs: [
      "Pressure, level, flow, temperature, humidity, vibration",
      "Energy & power quality meters",
      "Gas, leak, and environmental probes",
      "GPS / GNSS asset tracking",
      "Pre-bound drivers — zero integration work on site",
      "Online catalogue launching with the Nexus shop",
    ],
  },
];

// Integrations — extending Nexus beyond its own data.
export const NEXUS_INTEGRATIONS = [
  "Weather APIs — real-time and forecast feeds into rule logic",
  "Power utility tariffs and demand windows",
  "Mapping & GIS providers for site visualisation",
  "Asset registers and CMMS — Maximo, SAP PM, Fiix, UpKeep",
  "Messaging — Email, SMS, Slack, Microsoft Teams, WhatsApp",
  "Ticketing — Geminii Rogue Support, Jira Service Management, Zendesk",
  "Identity — Microsoft Entra ID, Google Workspace, Okta SSO",
  "Data warehouses — Snowflake, BigQuery, Azure Data Lake",
];
